import { useState } from "react";
import PageHeader from "../components/PageHeader";
import { FaChevronDown } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function Faqs() {
    const faqs = [
        {
            question: "How do I book a hotel through UAEDiscountedHotels?",
            answer:
                "Simply browse our hotel listings, select your preferred property, choose your dates, and complete the booking process. You will receive instant confirmation upon successful payment.",
        },
        {
            question: "Can I cancel or modify my booking?",
            answer:
                "Yes. Cancellation and modification policies vary depending on the hotel, airline, or service provider. Please review the booking terms before confirming your reservation.",
        },
        {
            question: "Do you provide visa assistance?",
            answer:
                "Yes, we offer professional visa assistance services for tourists, business travelers, and transit passengers. Our team will guide you through documentation and application requirements.",
        },
        {
            question: "Do you offer flight booking services?",
            answer:
                "Absolutely. We provide domestic and international flight bookings with access to multiple airlines, competitive fares, and flexible travel options.",
        },
        {
            question: "Are my payments secure?",
            answer:
                "Yes. All transactions are processed through secure encrypted payment gateways to ensure complete protection of your personal and financial information.",
        },
        {
            question: "Do you provide airport transfer services?",
            answer:
                "Yes. We arrange reliable airport transfers including private vehicles, shared transfers, and luxury transportation options.",
        },
        {
            question: "Can I book tours and activities?",
            answer:
                "Yes. We offer city tours, desert safaris, sightseeing experiences, attraction tickets, adventure activities, and customized travel experiences.",
        },
        {
            question: "Do you provide customer support?",
            answer:
                "Our dedicated support team is available to assist you with bookings, travel inquiries, modifications, and general customer service needs.",
        },
    ];

    const [activeIndex, setActiveIndex] = useState(null);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>Frequently Asked Questions | UAE Dubai Hotels</title>

                <meta
                    name="description"
                    content="Find answers to frequently asked questions about hotel bookings, payments, cancellations, refunds, check-in, check-out, and reservations at UAE Dubai Hotels."
                />

                <meta
                    name="keywords"
                    content="UAE Dubai Hotels FAQ, Dubai hotel booking FAQ, hotel reservations UAE, payment questions, cancellation policy, hotel booking help, travel FAQs UAE"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="UAE Dubai Hotels" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href="https://uaedubaihotels.com/faqs"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="UAE Dubai Hotels" />
                <meta property="og:locale" content="en_US" />

                <meta
                    property="og:title"
                    content="Frequently Asked Questions | UAE Dubai Hotels"
                />

                <meta
                    property="og:description"
                    content="Find answers to common questions about hotel bookings, payments, cancellations, reservations, and customer support at UAE Dubai Hotels."
                />

                <meta
                    property="og:url"
                    content="https://uaedubaihotels.com/faqs"
                />

                <meta
                    property="og:image"
                    content="https://uaedubaihotels.com/images/default-og.png"
                />

                {/* Twitter */}
                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Frequently Asked Questions | UAE Dubai Hotels"
                />

                <meta
                    name="twitter:description"
                    content="Get answers to frequently asked questions about hotel bookings, payments, cancellations, reservations, and more."
                />

                <meta
                    name="twitter:image"
                    content="https://uaedubaihotels.com/images/default-og.png"
                />
            </Helmet>


            <div className="
            min-h-screen
            bg-[#FCFAF5]
            dark:bg-gray-950
        ">


                {/* PAGE HEADER */}
                <PageHeader
                    title="Frequently Asked Questions"
                    subtitle="Everything you need to know about our hotel booking services"
                />



                {/* FAQ SECTION */}
                <section className="
                max-w-6xl
                mx-auto
                px-6
                py-20
            ">


                    {/* INTRO */}
                    <div className="
                    text-center
                    mb-14
                ">


                        <span className="
                        inline-block
                        px-4
                        py-1.5
                        rounded-full
                        bg-[#0F766E]/10
                        text-[#0F766E]
                        dark:text-[#B89442]
                        text-sm
                        font-semibold
                        tracking-wide
                    ">
                            Support Center
                        </span>



                        <h2 className="
                        mt-5
                        text-4xl
                        md:text-5xl
                        font-serif
                        text-gray-900
                        dark:text-white
                    ">
                            Frequently Asked Questions
                        </h2>



                        <p className="
                        mt-4
                        max-w-2xl
                        mx-auto
                        text-gray-600
                        dark:text-gray-400
                        leading-relaxed
                    ">
                            Find quick answers about hotel bookings,
                            payments, cancellations, and our services
                            across UAE.
                        </p>


                    </div>





                    {/* FAQ LIST */}
                    <div className="
                    space-y-5
                ">


                        {faqs.map((item, index) => (

                            <div
                                key={index}
                                className={`
                                bg-white
                                dark:bg-gray-900
                                rounded-[30px]
                                border
                                border-[#DDD2BA]
                                dark:border-gray-800
                                overflow-hidden

                                shadow-sm
                                hover:shadow-xl

                                transition-all
                                duration-300
                            `}
                            >



                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="
                                    w-full
                                    flex
                                    justify-between
                                    items-center
                                    px-6
                                    py-6
                                    text-left
                                    gap-4
                                "
                                >


                                    <span className="
                                    font-semibold
                                    text-gray-900
                                    dark:text-white
                                    text-lg
                                ">
                                        {item.question}
                                    </span>



                                    <div className={`
                                    w-11
                                    h-11
                                    rounded-2xl
                                    flex
                                    items-center
                                    justify-center

                                    bg-[#0F766E]
                                    text-white

                                    transition-all
                                    duration-300

                                    ${activeIndex === index
                                            ? "bg-[#B89442]"
                                            : ""
                                        }
                                `}>

                                        <FaChevronDown
                                            className={`
                                            transition-transform
                                            duration-300

                                            ${activeIndex === index
                                                    ? "rotate-180"
                                                    : ""
                                                }
                                        `}
                                        />

                                    </div>



                                </button>






                                <div
                                    className={`
                                    overflow-hidden
                                    transition-all
                                    duration-500

                                    ${activeIndex === index
                                            ? "max-h-96 opacity-100"
                                            : "max-h-0 opacity-0"
                                        }
                                `}
                                >


                                    <div className="
                                    px-6
                                    pb-6
                                    border-t
                                    border-[#DDD2BA]
                                    dark:border-gray-800
                                ">


                                        <p className="
                                        pt-5
                                        text-gray-600
                                        dark:text-gray-300
                                        leading-8
                                    ">
                                            {item.answer}
                                        </p>


                                    </div>


                                </div>



                            </div>

                        ))}


                    </div>


                </section>




                {/* BOTTOM CTA */}
                <section className="
                mx-6
                mb-20
                rounded-[45px]
                bg-[#0F766E]
                py-14
                px-6
                text-center
                text-white
            ">


                    <h2 className="
                    text-3xl
                    md:text-4xl
                    font-serif
                ">
                        Still Have Questions?
                    </h2>


                    <p className="
                    mt-4
                    text-white/80
                    max-w-xl
                    mx-auto
                ">
                        Our support team is ready to help you with
                        bookings, hotel deals, and travel assistance.
                    </p>



                    <button
                        className="
                        mt-7
                        px-8
                        py-3
                        rounded-full
                        bg-[#B89442]
                        hover:bg-white
                        hover:text-[#0F766E]
                        text-white
                        font-semibold
                        transition-all
                        duration-300
                    "
                    >
                        Contact Us
                    </button>


                </section>


            </div>
        </>
    );
}